/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {BaseDB_ApiGeneratorCaller} from "@intuitionrobotics/db-api-generator/frontend";
import {RoutingModule, ThunderDispatcher, ToastModule, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {ApiGetBigData, ApiGetBigDataForMany, ApiPatientLastUpdated, ApiPatientsLastUpdated, ExcelRow, windowType} from "@app/app-shared/api";
import {
    DB_PatientData,
    PsychologicalMemoryNames,
    PsychologicalQuestion,
    Request_PatientData,
    Request_PatientDataForMany,
    Response_PatientLastUpdate,
    ReturnType_PatientData,
    TableKeys
} from "@app/app-shared/patient-data";
import {GoalsConfigModule} from "@modules/GoalsConfigModule";
import {_clearTimeout, _setTimeout, currentTimeMillies, Day, deepClone, Hour, merge, Minute, ObjectTS, sortArray} from "@intuitionrobotics/ts-common";
import {BaseHttpRequest} from "@intuitionrobotics/thunderstorm/shared/BaseHttpRequest";
import {ErrorResponse} from "@intuitionrobotics/thunderstorm/shared/types";
import {PARAM_ALERT, PARAM_PATIENT, UserPreferencesModule} from "@modules/UserPreferencesModule";
import {BugReportModule} from "@intuitionrobotics/bug-report/frontend";
import {Platform_Slack} from "@intuitionrobotics/bug-report";
import dayjs from "../app/utils/dayjs";
import {Frequency, timeUnits} from "@app/app-shared/care_config";
import ExcelJS from "exceljs";
import {RemindersModule} from "@modules/RemindersModule";
import {formatAlertDatetime} from "../app/pages/ui/alerts/util";
import {DB_Patient} from "@app/app-shared/patient";
import {Route_Dashboard} from "../app/Routes";
import {RouteParams} from "@intuitionrobotics/thunderstorm/app-frontend/modules/routing/route";
import {DB_Organization} from "@app/ir-q-app-common/types/care-center/organization";
import {AnalyticConsts, AnalyticsModule} from "@modules/AnalyticsModule";

import {ContactsMap} from "../app/pages/ui/messages/ContactsContext";


export const navigateToPatientPage = (patient: DB_Patient, routeKey?: string, alertId?: string) => {
    const params: RouteParams = {
        [PARAM_PATIENT]: patient._id
    };
    if (alertId)
        params[PARAM_ALERT] = alertId;

    routeKey && UserPreferencesModule.setLastMainRoute(routeKey);
    AnalyticsModule.logCareManagerEvent({
        objectType: AnalyticConsts.ObjectTypes.CUSTOMERS,
        entity: AnalyticConsts.Entities.USER,
        activityStatus: AnalyticConsts.ActivityStatus.CLICKED,
        objectSubType: AnalyticConsts.ObjectSubTypes.DASHBOARD
    });

    RoutingModule.goToRoute(Route_Dashboard, params);
}

export type PsychologicalData = {
    date: number,
    results: { key: string, value: number }[]
}

export type PatientDataMap = {
    [unitId: string]: {
        [key: string]: DB_PatientData[]
    }
}

export type PatientSharingInfoMap = {
    [unitId: string]: {
        [key: string]: boolean
    }
}

type LastUserAction = {
    [unitId: string]: Response_PatientLastUpdate & { updatedTime: number } | null
}

export interface OnPatientDataUpdated {
    __onPatientDataUpdated: () => void;
}

export interface OnPatientDataUpdatedForMany {
    __onPatientDataUpdatedForMany: () => void;
}

export interface LastUpdated {
    __lastUpdated: () => void
}

export const RequestKey_GetPatientData = "get-patient-data";
export const RequestKey_GetPatientDataForMany = "get-patient-data-for-many";
export const RequestKey_InsertPatientData = "delete-patient-data";

export const dispatch_onPatientDataUpdated = new ThunderDispatcher<OnPatientDataUpdated, "__onPatientDataUpdated">("__onPatientDataUpdated");
export const dispatch_onPatientDataUpdatedForMany = new ThunderDispatcher<OnPatientDataUpdatedForMany, "__onPatientDataUpdatedForMany">("__onPatientDataUpdatedForMany");
export const dispatch_lastUpdated = new ThunderDispatcher<LastUpdated, "__lastUpdated">("__lastUpdated");
const defaultSleepMs = 5 * Minute

export class PatientDataModule_Class
    extends BaseDB_ApiGeneratorCaller<DB_PatientData> {

    async onEntryCreated(response: DB_PatientData): Promise<void> {
    }

    async onEntryUpdated(response: DB_PatientData): Promise<void> {
    }

    async onEntryDeleted(response: DB_PatientData): Promise<void> {
    }

    async onGotUnique(response: DB_PatientData): Promise<void> {
    }

    async onQueryReturned(response: DB_PatientData[]): Promise<void> {
    }

    private lastUpdatedTimestamp: {
        unitsLastUpdate: {
            [unitId: string]: number
        }
        timer?: number
        unitId?: string
    } = {unitsLastUpdate: {}};

    private inProgressMap: { [unitId: string]: boolean } = {};
    private sleepMs = defaultSleepMs;

    clearFetchingTimeout = (unitId: string) => {
        this.inProgressMap[unitId] = false;
        _clearTimeout(this.lastUpdatedTimestamp.timer);
        delete this.lastUpdatedTimestamp.timer
        delete this.lastUpdatedTimestamp.unitId
    };

    private patientData: PatientDataMap = {};
    private patientSharingInfo: PatientSharingInfoMap = {};

    // private lastUpdated: number | undefined = undefined;
    private lastUserAction: LastUserAction = {};

    getData = (unitId: string, key: TableKeys) => {
        if (!this.patientData || unitId === "" || !this.patientData[unitId])
            return;

        return {
            share_info: this.patientSharingInfo[unitId],
            data: this.patientData[unitId][key]?.filter((d) => d.timestamp >= UserPreferencesModule.getStartingDate().getTime())
        };
    };

    getLastUserAction = (unitId: string) => this.lastUserAction[unitId];

    getLastUserActionFormatted = (unitId: string, demo: boolean): { formattedDateTime: string, timestamp: number } => {
        if (demo) {
            const n = Math.ceil(Math.random() * 24 * 60);
            const lastInt = dayjs().add(-1 * n, "minutes");
            const now = dayjs();
            const _time = lastInt.format("hh:mm A");
            const _formattedDateTime = lastInt.isSame(now, "day") ? ` Today at ${_time}` : `Yesterday at ${_time}`;
            return {
                formattedDateTime: _formattedDateTime,
                timestamp: lastInt.valueOf()
            };
        }

        const lastUserAction = this.getLastUserAction(unitId);
        if (!lastUserAction || !lastUserAction.timestamp) {
            return {
                formattedDateTime: "n/a",
                timestamp: 0
            };
        }

        const timestamp = lastUserAction.timestamp;
        const {date} = formatAlertDatetime(timestamp);
        const time = dayjs(timestamp).format("hh:mm A");
        const formattedDateTime = date === " Today" ? ` Today at ${time}` : `${date} at ${time}`;

        return {
            formattedDateTime,
            timestamp
        };
    };

    spreadSelfReportData = (labels: string[], unitId?: string): DB_PatientData[] => {
        if (!unitId)
            return [];

        const data = this.getData(unitId, TableKeys.SelfReport);
        if (!data?.data)
            return [];

        const dataToSpread = deepClone(data.data).filter(_element => labels.includes(_element.label));
        sortArray(dataToSpread, _data => _data.timestamp, true);

        let l = 0;
        let r = 0;
        let spreadData: DB_PatientData[] = [];
        while (l < dataToSpread.length && r < dataToSpread.length) {
            const leftDay = dataToSpread[l].timestamp;
            const rightDay = dataToSpread[r].timestamp;
            if (dayjs(leftDay).isSame(dayjs(rightDay), "day"))
                ++r;

            if (!dayjs(leftDay).isSame(dayjs(rightDay), "day") || r === dataToSpread.length) {
                const daySubArray = dataToSpread.slice(l, r);

                spreadData = spreadData.concat(this.spreadByDay(daySubArray));
                l = r;
                // r++;
            }
        }
        return spreadData;
    };

    spreadByDay = (dayData: DB_PatientData[]) => {
        let dayDataToSpread = dayData;
        if (dayDataToSpread.length === 1) return dayDataToSpread;
        if (dayDataToSpread.length > 8) dayDataToSpread = dayData.slice(dayData.length - 8);
        for (let i = 0; i < dayDataToSpread.length; i++) {
            const dayDataToSpreadElement = dayDataToSpread[i + 1];
            const dayDataToSpreadElement1 = dayDataToSpread[i];
            if (dayDataToSpreadElement && dayDataToSpreadElement1 && dayDataToSpreadElement.timestamp - dayDataToSpreadElement1.timestamp < (Hour * 3)) {
                dayDataToSpreadElement.timestamp = dayDataToSpreadElement1.timestamp + (Hour * 3);
            }
        }
        if (dayjs(dayDataToSpread[0].timestamp).isSame(dayDataToSpread[dayDataToSpread.length - 1].timestamp, "day"))
            return dayDataToSpread;
        this.resetSpreadPerDay(dayDataToSpread);
        return dayDataToSpread;
    };

    resetSpreadPerDay = (dataToShift: DB_PatientData[]) => {
        const endOfDay = dayjs(dataToShift[0].timestamp).endOf("day").valueOf() - (Hour * 3);
        const diff = dataToShift[dataToShift.length - 1].timestamp - endOfDay;
        if (diff < 0) return;
        dataToShift[dataToShift.length - 1].timestamp = dataToShift[dataToShift.length - 1].timestamp - diff;
        for (let i = dataToShift.length - 1; i > 0; i--) {
            if (dataToShift[i].timestamp - dataToShift[i - 1].timestamp < (Hour * 3)) {
                dataToShift[i - 1].timestamp = dataToShift[i].timestamp - (Hour * 3);
            }
        }
    };

    private processAndSaveData = (response: ReturnType_PatientData, patient: DB_Patient) => {
        const _unitId = patient.unitId;
        this.patientData[_unitId] = this.patientData[_unitId] || {};
        if (response.data.length === 0)
            return;

        this.patientSharingInfo[_unitId] = merge(this.patientSharingInfo[_unitId] || {}, response.share_info);
        const timezone = patient.timezone;

        const now = dayjs().valueOf();
        // dayjs's .utcOffset() is minutes EAST-positive; moment-timezone's
        // zone.utcOffset() was minutes WEST-positive (Date.getTimezoneOffset
        // convention) — so negate to preserve the original sign and the
        // (patient - user) subtraction that follows.
        const zoneOffsetMinutes = (tz: string): number => {
            try {
                return -dayjs(now).tz(tz).utcOffset();
            } catch {
                return 0;
            }
        };
        const patientTimezoneOffSet: number = zoneOffsetMinutes(timezone);
        const userTimezoneOffSet: number = zoneOffsetMinutes(dayjs.tz.guess());
        const offset = Minute * (patientTimezoneOffSet - userTimezoneOffSet);

        response.data.forEach((_data: DB_PatientData) => {
            if (!this.patientData[_data.unitId])
                this.patientData[_data.unitId] = {}

            this.patientData[_data.unitId][_data.key] = this.patientData[_data.unitId][_data.key] || [];

            const patientDatumElement = this.patientData[_data.unitId][_data.key];

            if (!patientDatumElement)
                return;

            if (_data.key === TableKeys.SelfReport || _data.key === TableKeys.PsychologicalAssessment) {
                let _idx = patientDatumElement.findIndex(d => d._id === _data._id);
                if (_idx === -1)
                    _idx = patientDatumElement.length;

                _data.timestamp = dayjs(_data.timestamp).valueOf() - offset;
                return patientDatumElement[_idx] = _data;
            }

            if (_data.key === TableKeys.Pain) {
                let _idx = patientDatumElement.findIndex(d => d._id === _data._id);
                if (_idx === -1)
                    _idx = patientDatumElement.length;

                return patientDatumElement[_idx] = _data;
            }

            const idx = patientDatumElement.findIndex(_dataPoint => {
                if (_dataPoint.label !== _data.label)
                    return;

                return dayjs(_dataPoint.timestamp).isSame(dayjs(_data.timestamp), "day");
            });

            if (idx !== -1) {
                const existingDataAtDate = patientDatumElement[idx];
                if (_data.timestamp > existingDataAtDate.timestamp) {
                    if ([TableKeys.Stress, TableKeys.Exercise].includes(_data.key) && _data.label === existingDataAtDate.label && _data.value < existingDataAtDate.value) {
                        return;
                    }
                    _data.timestamp = dayjs(_data.timestamp).startOf("day").valueOf();
                    patientDatumElement[idx] = _data;
                    return;
                }
                return;
            }

            _data.timestamp = dayjs(_data.timestamp).startOf("day").valueOf();
            return patientDatumElement.push(_data);
        });

        const userMap = this.patientData[_unitId];
        if (!userMap)
            return;
    };

    getCondensedMap = (key: TableKeys, unitId: string, numberOfDays: number, label: string): DB_PatientData[] | undefined => {
        if (!this.patientData[unitId]?.[key])
            return;

        const sortedArray = sortArray(this.patientData[unitId][key], _data => _data.timestamp, true);
        const frequency = Day * numberOfDays;
        const dayStart = dayjs().startOf("day").valueOf();
        let currentMax = dayStart;
        let currentMin = dayStart - frequency;
        const newWeeklyArray: DB_PatientData[] = [];
        for (let i = sortedArray.length - 1; i >= 0; i--) {
            //this means we did not have an element for the given range
            while (sortedArray[i].timestamp < currentMin) {
                currentMax = currentMin;
                currentMin = currentMin - frequency;
            }
            if (sortedArray[i].label !== label)
                continue;

            if (sortedArray[i].timestamp >= currentMin && sortedArray[i].timestamp <= currentMax) {
                //since the array is sorted, the first element we encounter will be the greatest in each range. We push the max rather than the actual value
                //as this represents the week
                const elementCone = deepClone(sortedArray[i]);
                elementCone.timestamp = currentMax;
                newWeeklyArray.push(elementCone);
                //once we have encountered an element for the given range, we can change the range
                currentMax = currentMin;
                currentMin = currentMin - frequency;
            }
        }
        return newWeeklyArray;
    };

    getCareConfigs = (label: string, unitId?: string) => {
        if (!unitId)
            return;

        const patientConfigs = GoalsConfigModule.getByUnitId(unitId);
        return patientConfigs?.find(pc => pc.name === label);
    };

    getFrequency = (label: string, unitId: string) => {
        const careGoal = this.getCareConfigs(label, unitId)
        if (!careGoal)
            return undefined

        const frequency: Frequency =
            {
                type: "unit",
                times: careGoal.threshold,
                value: timeUnits[windowType.indexOf(careGoal.windowType)]
            }
        return frequency
    };


    fetchDataLazy = (patient: DB_Patient): any => {
        const unitId = patient.unitId;
        const oldUnitId = this.lastUpdatedTimestamp.unitId;
        if (oldUnitId !== unitId) {
            clearTimeout(this.lastUpdatedTimestamp.timer);
            this.lastUpdatedTimestamp = {
                ...this.lastUpdatedTimestamp,
                timer: undefined,
                unitId
            };
        }

        const unitLastUpdate = this.lastUpdatedTimestamp.unitsLastUpdate[unitId];
        if (unitLastUpdate) {
            const deltaToFetch = unitLastUpdate + this.sleepMs - currentTimeMillies();
            if (deltaToFetch > 0) {
                _clearTimeout(this.lastUpdatedTimestamp.timer);
                return this.lastUpdatedTimestamp.timer = _setTimeout(() => this.fetchDataLazy(patient), deltaToFetch);
            }
        }

        if (this.inProgressMap[unitId])
            return;

        this.inProgressMap[unitId] = true;
        const body: Request_PatientData = {
            unitId: unitId,
            // - Day is to take into account the time proxy
            startTime: unitLastUpdate ? (unitLastUpdate - Day) : currentTimeMillies() - (UserPreferencesModule.getTimeFilterSelectedOption().days * Day)
        };

        XhrHttpModule
            .createRequest<ApiGetBigData>(HttpMethod.POST, RequestKey_GetPatientData)
            .setJsonBody(body)
            .setRelativeUrl("/v1/graphs/data")
            // TODO: Probably need a retry
            .setOnError((request: BaseHttpRequest<ApiGetBigData>, resError?: ErrorResponse<any>) => {
                this.logWarning("failed to load data", resError);

                dispatch_onPatientDataUpdated.dispatchUI();
                _clearTimeout(this.lastUpdatedTimestamp.timer);
                this.lastUpdatedTimestamp.timer = _setTimeout(() => this.fetchDataLazy(patient), this.sleepMs);
            })
            .execute(response => {
                this.processAndSaveData(response, patient);
                dispatch_onPatientDataUpdated.dispatchUI();

                this.inProgressMap[unitId] = false;
                this.lastUpdatedTimestamp.unitsLastUpdate[unitId] = currentTimeMillies();
                _clearTimeout(this.lastUpdatedTimestamp.timer);
                // Need a better algo =)
                this.sleepMs = response.data.length === 0 ? Math.min(this.sleepMs * 2, 30 * Minute) : defaultSleepMs;
                this.lastUpdatedTimestamp.timer = _setTimeout(() => this.fetchDataLazy(patient), this.sleepMs);
            });
    };

    getLastUpdatedByPatient = (unitId: string) => {
        const updatedTime = this.lastUserAction[unitId]?.updatedTime;
        if (updatedTime && currentTimeMillies()  <= updatedTime + Minute)
            return;

        XhrHttpModule
            .createRequest<ApiPatientLastUpdated>(HttpMethod.POST, "patient_last_updated")
            .setJsonBody({id: unitId})
            .setRelativeUrl("/v1/graphs/patient-updated")
            .setOnError((request, resError) => {
                this.logError(`Failed to get latest updated patient timestamp for unit ${unitId}`, request, resError);
                BugReportModule.sendBugReport(`Failed to get latest updated patient timestamp for unit ${unitId}`, "", [Platform_Slack]);
            })
            .execute(response => {
                if (!response)
                    return;

                this.lastUserAction[unitId] = {...response, updatedTime: currentTimeMillies()};
                dispatch_lastUpdated.dispatchUI();

            });
    };
    //create new binder it will be post request with body
    fetchLastUpdatedByPatients = (_unitsId: string[]) => {
        const unitsId = _unitsId.filter(u => {
            const updatedTime = this.lastUserAction[u]?.updatedTime;
            if (!updatedTime)
                return true;
            return currentTimeMillies() - updatedTime > Minute;
        });

        if (unitsId.length === 0)
            return;

        XhrHttpModule
            .createRequest<ApiPatientsLastUpdated>(HttpMethod.POST, "patients_last_updated")
            .setJsonBody({unitsId})
            .setRelativeUrl("/v1/graphs/patients-updated")
            .setOnError((request, resError) => {
                this.logError(`Failed to get latest updated patient timestamp for unit `, request, resError);
                BugReportModule.sendBugReport(`Failed to get latest updated patient timestamp for unit `, "", [Platform_Slack]);
            })
            .execute(response => {
                const units = new Set(_unitsId);
                response.forEach(r => {
                    this.lastUserAction[r.agentId] = {timestamp: r.timestamp, lastPlan: r.planName, updatedTime: currentTimeMillies(), lastPositivePlanName: r.lastPositivePlanName};
                    units.delete(r.agentId);
                })
                units.forEach(u => this.lastUserAction[u] = {updatedTime: currentTimeMillies()});
                dispatch_lastUpdated.dispatchUI();

            });
    };

    preparePsychologicalAssessmentData = (psychData: DB_PatientData[] | undefined, questions: PsychologicalQuestion[], type: PsychologicalMemoryNames) => {
        if (!psychData?.length) {
            return [];
        }

        const planIdMap: { [planId: string]: PsychologicalData } = {};
        const sortedPsychologicalData = psychData.sort((a, b) => a.timestamp - b.timestamp);

        // Group by plan_id.
        // Iterate in descending timestamp order.
        for (const d of sortedPsychologicalData) {
            if (d.label !== type)
                continue;

            if (d.value === undefined)
                continue;

            const planId = d.extraData["planId"] as string;
            const questionKeys = questions.map(q => q.key);

            if (!planIdMap[planId]) {
                planIdMap[planId] = {date: d.timestamp, results: []};
            }

            // Skip entries for invalid questions.
            if (questionKeys.includes(d.extraData["sectionName"])) {
                const existing = planIdMap[planId].results.find(e => e.key === d.extraData["sectionName"]);
                if (existing) {
                    this.logWarning(`Found duplicate psychological assessment response in plan ${planId}`);
                    // Keep the existing which is latest.
                    continue;
                }
                planIdMap[planId].results.push({key: d.extraData["sectionName"], value: d.value});
            }
        }

        const groupedPerPlanId = Object.values(planIdMap);
        const sorted = groupedPerPlanId.sort((a, b) => b.date - a.date);

        const keepMax = 6;
        // if (!keepMax)
        // 	return [];

        let accepted = 0;
        const scoresDisplayed: { date: number, scores: number[] }[] = [];

        let foundValid: boolean;
        for (const entry of sorted) {
            const results = entry.results;
            const date = entry.date;

            foundValid = false;
            const scores: number[] = [];
            questions.forEach(question => {
                const foundResult = results.find(r => r.key === question.key);
                if (foundResult) {
                    foundValid = true;
                    scores.push(foundResult.value);
                } else {
                    scores.push(-1);
                }
            });

            if (foundValid) {
                accepted += 1;
                scoresDisplayed.unshift({date, scores});
            }

            if (accepted === keepMax)
                break;

        }

        return scoresDisplayed;
    };

    getAllPatientDataForMany = (contacts: ContactsMap, orgs: DB_Organization[], prefixExcelName: string, unitIds: string[], startTime?: number, endTime?: number) => {
        const orgsData = orgs.reduce((carry: { [_id: string]: ObjectTS }, o) => {
            const patientsOrganizationAndContacts = RemindersModule.getPatientsOrganizationAndContacts(contacts, o);

            carry[o._id] = {name: o.name, ...patientsOrganizationAndContacts};
            return carry;
        }, {});

        const body: Request_PatientDataForMany = {
            unitIds,
            startTime: startTime || currentTimeMillies() - (UserPreferencesModule.getTimeFilterSelectedOption().days * Day),
            endTime: endTime,
            orgsData
        };

        XhrHttpModule
            .createRequest<ApiGetBigDataForMany>(HttpMethod.POST, RequestKey_GetPatientDataForMany)
            .setJsonBody(body)
            .setRelativeUrl("/v1/graphs/data/for-many")
            .setOnError((request: BaseHttpRequest<ApiGetBigDataForMany>, resError?: ErrorResponse<any>) => {
                this.logWarning("failed to load data", resError);
                dispatch_onPatientDataUpdatedForMany.dispatchUI();
            }).execute(response => {
            this.downloadCSV(prefixExcelName, response.result)
            dispatch_onPatientDataUpdatedForMany.dispatchUI();
        });
    }

    downloadCSV(prefixExcelName: string, dataForMany?: ExcelRow[]) {
        if (!dataForMany)
            return;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("My Sheet");
        sheet.properties.defaultRowHeight = 80;
        sheet.properties.defaultColWidth = 50;

        sheet.columns = [
            {
                header: "",
                key: "A",
                width: 20
            },
            {
                header: "",
                key: "B",
                width: 20
            },
            {
                header: "",
                key: "C",
                width: 30
            }
        ]


        if (!dataForMany.length)
            return ToastModule.toastInfo("No data to export for this/these customer(s)");

        const headerRow = sheet.getRow(1);
        headerRow.hidden = true;

        sheet.addRows(dataForMany);

        workbook.eachSheet((worksheet) => {
            worksheet.eachRow((row) => {
                let shouldChangeColor = false;
                row.eachCell((cell) => {
                    if (cell.value === "Date")
                        shouldChangeColor = true;

                    // @ts-ignore
                    if (this.getColorForCell(cell.value)) {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            // @ts-ignore
                            fgColor: {argb: this.getColorForCell(cell.value)}
                        };
                    } else { // @ts-ignore
                        if (cell.value !== "" && cell.col === 1) {
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: {argb: "ffebecf0"}
                            };
                        }
                    }

                    cell.border = {
                        top: {style: 'thin'},
                        left: {style: 'thin'},
                        bottom: {style: 'thin'},
                        right: {style: 'thin'}
                    };
                });
                if (shouldChangeColor) {
                    row.eachCell((cell) => {
                        cell.fill = {
                            type: 'pattern',
                            pattern: 'solid',
                            fgColor: {argb: 'ff4a86e8'} // Blue color
                        };
                    });
                }
            });
        });

        workbook.xlsx.writeBuffer().then((data) => {
            const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = url;
            anchor.download = (prefixExcelName || "") + '_measurements_output.xlsx';
            anchor.click();
            window.URL.revokeObjectURL(url);
        }).catch(error => this.logError(error));
    }

    private getColorForCell(cell?: string) {
        switch (cell) {
            case "Customer":
            case "Report Date":
            case "Unit Name":
            case "Organization":
                return "ffcccccc"
            case "Date":
                return "ff4a86e8"
            default:
                return undefined;
        }
    }

    setData(data: PatientDataMap) {
        this.patientData = data;
        dispatch_onPatientDataUpdatedForMany.dispatchUI();
    }

}

export const PatientsDataModule = new PatientDataModule_Class({key: "patient-data", relativeUrl: ""}, "PatientsDataModule");
